/**
 * `HoverTip` — ONE tooltip for a whole grid, driven by `data-tip` on the cells.
 *
 * The period boards (`/attendance`, `/meal-allowance`) draw 31 days × every rostered worker —
 * 1,200 to 1,900 cells — and each cell used to wrap its content in an antd `<Tooltip>`. That
 * is not free: every `Tooltip` is a `Trigger` with its own state, cloned child, refs and
 * mouse/focus handlers, so a board render was several thousand React component instances
 * before a single cell had been drawn, and every re-render — a tick's pending flag, a filter
 * chip, a refetch — paid it again. The office felt it as lag on the two screens they hover
 * most.
 *
 * This is event delegation instead. A cell says what it has to say in a `data-tip` attribute
 * (a plain string, which is all these tooltips ever were), and ONE `HoverTip` mounted beside
 * the grid listens on the scroll container: on `mouseover` it finds the nearest `[data-tip]`
 * ancestor and, after a short delay, shows one fixed-position bubble above it. The state lives
 * HERE, so hovering re-renders this component and nothing else — the grid does not know it is
 * being hovered. Native `title` would also be one-instance-free, but its ~1 s delay and
 * unstyled box read as broken next to antd's tooltips elsewhere on the page.
 *
 * Kept deliberately small: no arrow, no placement options, no rich content. A cell that needs
 * more than a sentence is not a cell.
 *
 * Usage:
 *   const scroller = useRef<HTMLDivElement>(null);
 *   <div ref={scroller} className="cm-scroll">…<td data-tip="…">…</td>…</div>
 *   <HoverTip scope={scroller} />
 */
import { useEffect, useState, type CSSProperties, type RefObject } from 'react';
import { pageZoom } from '@/lib/text-size';

export interface HoverTipProps {
  /** The element whose descendants carry `data-tip`. Listeners attach to it, not to `document`. */
  scope: RefObject<HTMLElement | null>;
  /** Hover-to-show delay. antd uses 100 ms; slightly more keeps a fast sweep across a row quiet. */
  delayMs?: number;
}

interface Shown {
  text: string;
  /** Viewport-relative anchor, from `getBoundingClientRect()` at show time, in CSS pixels. */
  left: number;
  top: number;
  bottom: number;
}

const SHOW_DELAY_MS = 150;
const MAX_WIDTH = 300;
const GAP = 6;

export function HoverTip({ scope, delayMs = SHOW_DELAY_MS }: HoverTipProps) {
  const [shown, setShown] = useState<Shown | null>(null);

  useEffect(() => {
    const root = scope.current;
    if (root === null) return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let current: Element | null = null;

    const clear = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      current = null;
      setShown(null);
    };

    const show = (el: Element): void => {
      const text = el.getAttribute('data-tip');
      if (text === null || text === '') return;
      /* The rect is in VISUAL pixels, but the bubble's `left` / `top` are CSS pixels inside the
       * zoomed `<html>` and get multiplied by the Text size zoom again — on `Larger` a tip
       * aimed at x = 800 landed at 1,000. Back to CSS pixels once, here (lib/text-size.ts). */
      const r = el.getBoundingClientRect();
      const z = pageZoom();
      setShown({ text, left: (r.left + r.width / 2) / z, top: r.top / z, bottom: r.bottom / z });
    };

    const onOver = (e: Event): void => {
      const target = e.target instanceof Element ? e.target.closest('[data-tip]') : null;
      if (target === current) return;
      if (timer !== null) clearTimeout(timer);
      current = target;
      if (target === null) {
        timer = null;
        setShown(null);
        return;
      }
      timer = setTimeout(() => show(target), delayMs);
    };

    /* `mouseout` fires on every child boundary too; only a real exit of the tipped element
     * counts, and `relatedTarget` says where the pointer went. */
    const onOut = (e: Event): void => {
      if (current === null) return;
      const to = (e as MouseEvent).relatedTarget;
      if (to instanceof Node && current.contains(to)) return;
      clear();
    };

    /* Keyboard parity for anything focusable that carries a tip (the boards' cells are not,
     * today; the header buttons are). Same code path, no delay. */
    const onFocusIn = (e: Event): void => {
      const target = e.target instanceof Element ? e.target.closest('[data-tip]') : null;
      if (target === null) return;
      current = target;
      show(target);
    };

    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseout', onOut);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', clear);
    /* The bubble is `position: fixed` against an anchor measured once; any scroll moves the
     * anchor out from under it, so hide rather than chase. `capture` catches the inner
     * scroller as well as the window. */
    window.addEventListener('scroll', clear, true);

    return () => {
      if (timer !== null) clearTimeout(timer);
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseout', onOut);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', clear);
      window.removeEventListener('scroll', clear, true);
    };
  }, [scope, delayMs]);

  if (shown === null) return null;

  /* Above the cell, centred, clamped to the viewport; below it when the top row has no room. */
  const vw = typeof window === 'undefined' ? 1200 : window.innerWidth / pageZoom();
  const half = MAX_WIDTH / 2;
  const left = Math.min(Math.max(shown.left, half + 8), vw - half - 8);
  const flip = shown.top < 56;

  return (
    <div
      role="tooltip"
      style={{
        ...bubble,
        left,
        ...(flip ? { top: shown.bottom + GAP } : { top: shown.top - GAP }),
        transform: flip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      }}
    >
      {shown.text}
    </div>
  );
}

const bubble: CSSProperties = {
  position: 'fixed',
  zIndex: 1070,
  /* `max-content`, explicitly: a fixed box with `left` set and `width: auto` shrinks to fit
   * the space between `left` and the viewport's right edge, so a tip anchored on the last
   * few day columns came out 140 px wide and six lines tall. Sized to its text, then capped. */
  width: 'max-content',
  maxWidth: MAX_WIDTH,
  padding: '6px 8px',
  borderRadius: 6,
  background: 'rgba(0, 0, 0, 0.85)',
  color: '#fff',
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12)',
  pointerEvents: 'none',
};
